import { Pipe, PipeTransform } from '@angular/core';
import { Observable, of } from 'rxjs';
import { FaceStrongDetectionService } from 'ZalamoLibrary/src/lib/zalamo/face-detection/face-strong-detection.service';
import { map } from 'rxjs/operators';
import { ImagesContainer } from '@ui/orders/graphql';
import { ZalamoObject } from 'Upload/upload/graphql';

const eyePosition = (
  positions: {
    x: { min: number; max: number };
    y: { min: number; max: number };
  },
  rawBox,
  box,
) => {
  const width = box[2];
  const height = box[3];
  const widthInPercentage = rawBox[2];
  const heightInPercentage = rawBox[3];
  const eyeXCenter = (positions.x.max + positions.x.min) / 2;
  const eyeYCenter = (positions.y.max + positions.y.min) / 2;
  return {
    x: (eyeXCenter / box[0]) * rawBox[0],
    y: (eyeYCenter / box[1]) * rawBox[1],
  };
};

const calcTabloSquare = (
  {
    betweenEyes,
    ...faces
  }: {
    betweenEyes: {
      x: number;
      y: number;
    };
    faces: {
      embedding: [];
      rotation: {
        angle: {
          roll: number;
          yaw: number;
          pitch: number;
        };
      };
      boxRaw: number[];
      box: number[];
    }[];
    rightEye: {
      x: number;
      y: number;
    };
    leftEye: {
      x: number;
      y: number;
    };
  },
  targetWidth: number,
  targetHeight: number,
  currentImageSize: {
    width: number;
    height: number;
  },
  eyesYPosition = 45,
  eyesDistance = 150,
) => {
  const expectedEyesTopPosition = (eyesYPosition / 100) * targetHeight;

  let scale = Math.max(1 / faces.faces[0].boxRaw[2], 1);
  const eyesPositionDiff = eyesYPosition / 100 - betweenEyes.y;
  let imageRealHeight =
    targetWidth * (currentImageSize.height / currentImageSize.width);
  const targetAspectRatio = targetWidth / targetHeight;
  const currentImageAspectRatio =
    currentImageSize.width / currentImageSize.height;
  let imageRealWidth = imageRealHeight * currentImageAspectRatio;
  const afterZoomEyesPosition = betweenEyes.y * scale * imageRealHeight;
  const expectedEyesPosition =
    expectedEyesTopPosition * scale * imageRealHeight;
  const imageHeightAfterZoom = imageRealHeight * scale;
  const diffY = (targetHeight - targetWidth) / 2 / scale;
  let diffX = (targetWidth - imageRealWidth * faces.faces[0].boxRaw[2]) / 2;

  let y = Math.min(
    0,
    -1 *
      (targetHeight * faces.faces[0].boxRaw[1]) *
      (targetAspectRatio / currentImageAspectRatio) +
      diffY,
  );
  let x = -faces.faces[0].boxRaw[0] * targetWidth;
  if (imageHeightAfterZoom < targetHeight) {
    y = 0;
    scale = currentImageAspectRatio / targetAspectRatio;
    imageRealHeight = (targetWidth / currentImageAspectRatio) * scale;
    imageRealWidth = imageRealHeight * currentImageAspectRatio;
    diffX =
      (targetWidth - imageRealWidth * faces.faces[0].boxRaw[2]) / 2 / scale;

    x = -1 * (targetWidth * faces.faces[0].boxRaw[0]) + diffX;
  }
  if (imageRealHeight - y > targetHeight) {
    // y += Math.abs(imageRealHeight + y - targetHeight);
  }
  if (targetAspectRatio < faces.faces[0].boxRaw[2]) {
    // x += (targetWidth * (faces.faces[0].boxRaw[2] - targetAspectRatio)) / 2;
  }
  return {
    x: Math.min(0, x),
    y,
    w: 20,
    h: 20,
    scale,
    betweenEyes,

    rotations: faces.faces[0].rotation.angle,
    center: {
      x: faces.faces[0].boxRaw[0] + faces.faces[0].boxRaw[2] / 2,
      // * (1 +
      //   faces.faces[0].boxRaw[0] *
      //     Math.abs(faces.faces[0].rotation.angle.roll) *
      //     5.3),

      y: betweenEyes.y,
    },
    expectedEyesTopPosition,
    currentEyesY: betweenEyes.y * currentImageSize.height,
  };
};

@Pipe({
    name: 'tabloFaces',
    standalone: false
})
export class TabloFacesPipe implements PipeTransform {
  constructor(private faceStrongDetectionService: FaceStrongDetectionService) {}

  transform(
    photoId: string,
    photoGroupId: string,
    containerRatio = 1,
    contextId,
    photo: {
      width: number;
      height: number;
      context?: ZalamoObject;
      fromHand?: boolean;
    },
    targetWidth: number,
    targetHeight: number,
    currentImageSize: {
      width: number;
      height: number;
    },
    eyesPosition = 45,
  ): Observable<any> {
    if (!photoId || !photoGroupId) {
      return of(null);
    }
    if (photo.fromHand || photoId.startsWith('private')) {
      return of({
        tabloSquare: {
          x: 0,
          y: 0,
          w: 20,
          h: 20,
          scale: 1,
          betweenEyes: {
            x: 0,
            y: 0,
          },
          rotations: {
            roll: 0,
            yaw: 0,
            pitch: 0,
          },
          center: {
            x: 0,
            y: 0,
          },
          expectedEyesTopPosition: 0,
          currentEyesY: 0,
        },
      });
    }
    return this.faceStrongDetectionService
      .detectFacesByPhotoId(
        photoId,
        photoGroupId,
        2.5 / 3,
        contextId,
        photo,
        photo.context || ImagesContainer.PublicSession,
      )
      .pipe(
        map(({ faces, ...res }) => ({
          ...res,
          faces,
          silhouette: faces[0].annotations.silhouette.map((v) => [
            (100 * v[0]) / currentImageSize.width,
            (100 * v[1]) / currentImageSize.height,
          ]),
          rightEye: eyePosition(
            [
              faces[0].annotations.rightEyeLower0,
              faces[0].annotations.rightEyeUpper1,
              faces[0].annotations.rightEyeLower1,
              faces[0].annotations.rightEyeUpper2,
              faces[0].annotations.rightEyeLower2,
              faces[0].annotations.rightEyeLower3,
            ]
              .flatMap((v) => v)
              .reduce(
                (previousValue, currentValue) => {
                  if (currentValue[0] > previousValue.x.max) {
                    previousValue.x.max = currentValue[0];
                  }
                  if (currentValue[0] < previousValue.x.min) {
                    previousValue.x.min = currentValue[0];
                  }
                  if (currentValue[1] > previousValue.y.max) {
                    previousValue.y.max = currentValue[1];
                  }
                  if (currentValue[1] < previousValue.y.min) {
                    previousValue.y.min = currentValue[1];
                  }
                  return previousValue;
                },
                { x: { max: 0, min: 99999 }, y: { max: 0, min: 99999 } },
              ),
            faces[0].boxRaw,
            faces[0].box,
          ),
          leftEye: eyePosition(
            [
              faces[0].annotations.leftEyeUpper0,
              faces[0].annotations.leftEyeLower0,
              faces[0].annotations.leftEyeUpper1,
              faces[0].annotations.leftEyeLower1,
              faces[0].annotations.leftEyeUpper2,
              faces[0].annotations.leftEyeLower2,
              faces[0].annotations.leftEyeLower3,
            ]
              .flatMap((v) => v)
              .reduce(
                (previousValue, currentValue) => {
                  if (currentValue[0] > previousValue.x.max) {
                    previousValue.x.max = currentValue[0];
                  }
                  if (currentValue[0] < previousValue.x.min) {
                    previousValue.x.min = currentValue[0];
                  }
                  if (currentValue[1] > previousValue.y.max) {
                    previousValue.y.max = currentValue[1];
                  }
                  if (currentValue[1] < previousValue.y.min) {
                    previousValue.y.min = currentValue[1];
                  }
                  return previousValue;
                },
                { x: { max: 0, min: 99999 }, y: { max: 0, min: 99999 } },
              ),
            faces[0].boxRaw,
            faces[0].box,
          ),
        })),
        map((v) => ({
          ...v,
          betweenEyes: {
            x: (v.rightEye.x + v.leftEye.x) / 2,
            y: (v.rightEye.y + v.leftEye.y) / 2,
          },
        })),
        map((v) => ({
          ...v,
          tabloSquare: calcTabloSquare(
            v,
            targetWidth,
            targetHeight,
            currentImageSize,
            eyesPosition,
          ),
        })),
      );
  }
}
